import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { HyphenPipe } from '../../../../pipes/hyphen.pipe';
import { Api } from '../../../../services/api/api.service';
import { ViewService } from '../../../../services/view/view.service';
import { LinkLabelComponent } from '../../../core/labels/link-label.component';
import { TagComponent, TagType } from '../../../core/labels/tag.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { EmptyStateComponent } from '../../../core/panels/empty-state.component';
import { Day } from './month-view.component';

@Component({
  selector: 'app-absence-detail',
  imports: [
    CommonModule,
    EmptyStateComponent,
    DetailPanelComponent,
    DetailItemComponent,
    HyphenPipe,
    TagComponent,
    LinkLabelComponent,
  ],
  template: `
    @if (day) {
      @if (formattedItems.length === 0) {
        <app-empty-state
          label="No absence"
          [message]="day.date | date: 'MMM d, yyyy'"
          icon="fa-thin fa-calendar-day"
        />
      } @else {
        @for (item of formattedItems; track $index; let first = $first) {
          @if (!first) {
            <div class="tw-border-t tw-mt-3 tw-pt-2"></div>
          }

          <app-detail-panel
            [dataSource]="item"
            labelPosition="Top"
            [showEmpty]="false"
          >
            <app-detail-item label="Claim number" name="absenceNumber">
              <ng-template #value let-data>
                <app-link-label
                  (click)="navigateToClaim(data.absenceNumber)"
                  [ariaLabel]="'Open Claim Summary for ' + data.absenceNumber"
                >
                  {{ data.absenceNumber }}
                </app-link-label>
              </ng-template>
            </app-detail-item>
            <app-detail-item label="Date of absence" name="date" format="d" />
            <app-detail-item label="Type" name="type" />
            <app-detail-item label="Hours absent" name="hoursAbsent">
              <ng-template #value let-data>
                {{ data.formattedHoursAbsent }}
              </ng-template>
            </app-detail-item>
            <app-detail-item
              label="Confirmation number"
              name="absenceConfirmationNumber"
            />
            <app-detail-item label="Status" name="status">
              <ng-template #value let-data>
                @if (data.status === 'Event') {
                  <app-tag [type]="getTagType(data.status)">
                    {{ data.subStatus || 'Event' }}
                  </app-tag>
                } @else {
                  <app-tag [type]="getTagType(data.status)">
                    {{ data.status | hyphen: data.subStatus }}
                  </app-tag>
                }
              </ng-template>
            </app-detail-item>
            <app-detail-item label="Reason" name="absenceReason" />
          </app-detail-panel>
        }
      }
    }
  `,
  styles: ``,
})
export class AbsenceDetailComponent implements OnChanges {
  private view = inject(ViewService);
  private api = inject(Api);
  private router = inject(Router);

  @Input()
  day: Day;

  formattedItems: any[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['day']) {
      const items = this.getItems();
      this.formattedItems = items.map((item) => ({
        ...item,
        formattedHoursAbsent: this.formatHoursAbsent(
          item.hoursAbsent,
          item.minutesAbsent,
        ),
      }));
    }
  }

  getItems(): any[] {
    if (!this.day?.data) {
      return [];
    } else if (Array.isArray(this.day.data)) {
      return this.day.data;
    } else {
      return [this.day.data];
    }
  }

  private formatHoursAbsent(
    hoursAbsent: string | number | null | undefined,
    minutesAbsent: string | number | null | undefined,
  ): string {
    const parsedHours = this.parseNumeric(hoursAbsent);
    const parsedMinutes = this.parseNumeric(minutesAbsent);

    if (parsedHours == null && parsedMinutes == null) {
      return '';
    }

    const totalMinutes =
      parsedMinutes != null
        ? (parsedHours ?? 0) * 60 + parsedMinutes
        : (parsedHours ?? 0) * 60;

    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);

    if (minutes === 60) {
      return `${hours + 1} ${hours + 1 === 1 ? 'hour' : 'hours'} 0 minutes`;
    }

    const hoursPart = `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    const minutesPart = `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;

    return `${hoursPart} ${minutesPart}`;
  }

  private parseNumeric(
    value: string | number | null | undefined,
  ): number | null {
    if (value == null || value === '') {
      return null;
    }

    const parsed =
      typeof value === 'number' ? value : parseFloat(String(value));
    return Number.isFinite(parsed) ? parsed : null;
  }

  getTagType(status: string): TagType {
    switch (status) {
      case 'Approved':
        return 'Success';
      case 'Denied':
        return 'Error';
      case 'Pending':
        return 'Warn';
      case 'Event':
        return 'Teal';
      default:
        return 'Grey';
    }
  }

  async navigateToClaim(caseNumber: string): Promise<void> {
    if (!caseNumber) {
      return;
    }

    const currentClaim = this.view.currentClaim();
    if (currentClaim?.fileNumber === caseNumber) {
      this.view.navigateCurrentClaim([]);
      return;
    }

    const searchInput: any = {
      claimNumber: caseNumber,
      searchOptions: [],
      claimStatus: [],
      claimSubstatus: [],
      lob: [],
      claimType: [],
      caseType: [],
      claimFlags: [],
      ExactXRefMatchFlag: true,
    };
    const searchResult = await this.api.search.post(searchInput);

    if (searchResult?.rows?.length === 1) {
      await this.view.navigateClaim(searchResult.rows[0]);
      return;
    }

    this.router.navigate(['/view'], {
      queryParams: {
        tab: 'search',
        quicksearch: JSON.stringify({
          label: `Claim/Event number begins with <b>${caseNumber}</b>`,
          type: 'claim-event-number',
          value: caseNumber,
        }),
      },
      queryParamsHandling: 'merge',
    });
  }
}
